import {
  useGetInitiativeUniversalTrackersQuery,
  useOverrideAggregationMutation,
} from '@api/initiative-universal-trackers';
import { InitiativeUniversalTracker, AggregationOverrideData } from '@g17eco/types/initiativeUniversalTracker';
import { getInitialAggregationOverrideData } from './utils';
import { useMemo } from 'react';
import { useAppSelector } from '@reducers/index';
import { isStaff } from '@selectors/user';
import { AggregationOverrideForm } from './AggregationOverrideForm';
import { BulkActionUtr } from '@g17eco/types/survey-question-list';

interface Props {
  handleReload: (props?: {
    reloadSurvey?: boolean;
    closeModal?: boolean;
    scrollToTop?: boolean;
  }) => Promise<void | undefined>;
  selectedQuestion: BulkActionUtr | undefined;
  initiativeId: string;
  handleCloseModal: () => void;
}

export const AggregationOverrideContainer = (props: Props) => {
  const { handleReload, selectedQuestion, initiativeId, handleCloseModal } = props;
  const isStaffUser = useAppSelector(isStaff);
  const allowRender = isStaffUser && !!initiativeId && !!selectedQuestion;
  const { data: initiativeUtrs, isFetching } = useGetInitiativeUniversalTrackersQuery(initiativeId, {
    skip: !allowRender,
  });

  const initiativeUtrMap = useMemo(() => {
    return new Map<string, InitiativeUniversalTracker>(
      (initiativeUtrs ?? []).map((utr: InitiativeUniversalTracker) => [utr.universalTrackerId, utr]),
    );
  }, [initiativeUtrs]);

  const [updateAggregationOverride, { isLoading: isUpdating, error }] = useOverrideAggregationMutation();

  const isLoading = isFetching || isUpdating;

  if (!allowRender) {
    return null;
  }

  const initialData = getInitialAggregationOverrideData({ initiativeUtrMap, utr: selectedQuestion });

  const handleUpdateAggregationOverride = async ({ aggregationConfig, table }: AggregationOverrideData) => {
    updateAggregationOverride({
      aggregationConfig,
      table,
      initiativeId,
      utrId: selectedQuestion._id,
    })
      .unwrap()
      .then(() => {
        handleReload({ reloadSurvey: true, closeModal: true });
      })
      .catch(() => {
        handleReload({ scrollToTop: true });
      });
  };

  return (
    <AggregationOverrideForm
      key={JSON.stringify({ initialData })}
      isLoading={isLoading}
      error={error}
      handleCloseModal={handleCloseModal}
      selectedQuestion={selectedQuestion}
      initialData={initialData}
      handleUpdateAggregationOverride={handleUpdateAggregationOverride}
      initiativeUtrMap={initiativeUtrMap}
      rootInitiativeId={initiativeId}
    />
  );
};
